import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthUtils } from './auth.utils';
import { AuthAppService } from '@modules/auth';
import { AppSettingService } from '@core/settings/app-setting.service';
import { Observable, ReplaySubject, catchError, finalize, throwError, switchMap, map } from 'rxjs';

/**
 * Endpoints excluded from automatic 401 handling and token refresh
 * These paths should handle authentication errors manually
 */
const AUTH_EXCLUDED_ENDPOINTS = [
    '/api/auth/login',
    '/api/auth/logout',
    '/api/auth/register',
    '/api/auth/refresh-token',
    '/api/auth/forgot-password',
] as const;

interface RefreshState {
    subject: ReplaySubject<boolean>;
    subscribers: number;
    failed: boolean;
}

const refreshInFlightByService = new WeakMap<AuthAppService, RefreshState>();
const refreshFailureHandledByService = new WeakMap<AuthAppService, boolean>();

function handleRefreshFailure(authService: AuthAppService, onFailure: () => void): void {
    if (refreshFailureHandledByService.get(authService)) {
        return;
    }

    refreshFailureHandledByService.set(authService, true);
    const signOutResult = authService.signOut();
    if (signOutResult && typeof (signOutResult as { subscribe?: unknown }).subscribe === 'function') {
        (signOutResult as { subscribe: (callbacks: { error?: (error: unknown) => void }) => unknown }).subscribe({
            error: () => undefined,
        });
    }
    onFailure();
}

/**
 * Attempt a token refresh and retry the original request on success.
 * Extracted at module level to keep nesting depth ≤ 4.
 */
function refreshAndRetry(
    originalError: HttpErrorResponse,
    request: HttpRequest<unknown>,
    next: HttpHandlerFn,
    authService: AuthAppService,
    onFailure: () => void
): Observable<HttpEvent<unknown>> {
    const existingRefreshState = refreshInFlightByService.get(authService);
    if (!existingRefreshState) {
        refreshFailureHandledByService.delete(authService);

        const sharedRefreshState: RefreshState = {
            subject: new ReplaySubject<boolean>(1),
            subscribers: 0,
            failed: false,
        };
        refreshInFlightByService.set(authService, sharedRefreshState);

        authService.refreshCurrentToken().pipe(
            map((success) => {
                if (!success) {
                    handleRefreshFailure(authService, onFailure);
                    return false;
                }
                return true;
            }),
            catchError((refreshError) => {
                handleRefreshFailure(authService, onFailure);
                return throwError(() => refreshError);
            })
        ).subscribe({
            next: (success) => {
                sharedRefreshState.subject.next(success);
                sharedRefreshState.subject.complete();
            },
            error: (refreshError) => {
                sharedRefreshState.failed = true;
                sharedRefreshState.subject.error(refreshError);
                queueMicrotask(() => {
                    if (refreshInFlightByService.get(authService) === sharedRefreshState) {
                        refreshInFlightByService.delete(authService);
                    }
                });
            },
        });
    }

    const refreshState = refreshInFlightByService.get(authService) as RefreshState;
    refreshState.subscribers += 1;

    return refreshState.subject.asObservable().pipe(
        switchMap((success) => {
            if (success) {
                return next(request);
            }
            return throwError(() => originalError);
        }),
        catchError((error) => {
            return throwError(() => error);
        }),
        finalize(() => {
            refreshState.subscribers = Math.max(0, refreshState.subscribers - 1);
            if (refreshState.subscribers === 0 && !refreshState.failed) {
                refreshInFlightByService.delete(authService);
            }
        })
    );
}

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthAppService);
    const appSettingService = inject(AppSettingService);
    const router = inject(Router);

    const redirectToSignIn = (): void => {
        const redirectURL = location.pathname + location.search;
        router.navigate(['/sign-in'], { queryParams: { redirectURL } });
    };

    const addAuthHeader = (request: HttpRequest<unknown>, token: string): HttpRequest<unknown> => {
        return request.clone({
            headers: request.headers.set('Authorization', `Bearer ${token}`)
        });
    };

    const shouldAttachAuthHeader = (requestUrl: string): boolean => {
        try {
            const approvedOrigins = new Set<string>();
            const currentOrigin = globalThis.location?.origin;
            if (currentOrigin) {
                approvedOrigins.add(currentOrigin);
            }

            const configuredApiUrl = appSettingService.getApiUrl();
            if (configuredApiUrl) {
                try {
                    approvedOrigins.add(new URL(configuredApiUrl).origin);
                } catch {
                    // Ignore invalid configured API URLs and fall back to same-origin checks.
                }
            }

            const parsedUrl = new URL(requestUrl, currentOrigin ?? 'http://localhost');
            const isApiPath = parsedUrl.pathname === '/api' || parsedUrl.pathname.startsWith('/api/');
            return isApiPath && approvedOrigins.has(parsedUrl.origin);
        } catch {
            return requestUrl.startsWith('/api/');
        }
    };

    const isExcludedEndpoint = AUTH_EXCLUDED_ENDPOINTS.some(excludedPath =>
        req.url.toLowerCase().includes(excludedPath.toLowerCase())
    );

    const proceedWithRequest = (request: HttpRequest<unknown>): Observable<HttpEvent<unknown>> => {
        return next(request).pipe(
            catchError((error) => {
                // Catch "401 Unauthorized" responses
                if (!isExcludedEndpoint && error instanceof HttpErrorResponse && error.status === 401) {
                    return refreshAndRetry(error, request, next, authService, redirectToSignIn);
                }
                return throwError(() => error);
            })
        );
    };

    if (!shouldAttachAuthHeader(req.url) || isExcludedEndpoint) {
        return proceedWithRequest(req);
    }

    // Token is valid — add it and proceed
    if (!!authService.aToken && !AuthUtils.isTokenExpired(authService.aToken)) {
        return proceedWithRequest(addAuthHeader(req, authService.aToken));
    }

    // Access token is expired or missing: refresh the session through the cookie-based flow.
    if (!authService.aToken) {
        return proceedWithRequest(req);
    }

    return authService.refreshCurrentToken().pipe(
        switchMap((success) => {
            if (success && authService.aToken) {
                return proceedWithRequest(addAuthHeader(req, authService.aToken));
            }
            authService.signOut().subscribe();
            redirectToSignIn();
            return throwError(() => new Error('Token refresh failed'));
        }),
        catchError((err) => {
            authService.signOut().subscribe();
            redirectToSignIn();
            return throwError(() => err);
        })
    );
};


